import axios from "axios";
import { PaginatedResponse } from "../types/api";
import { Label } from "../types/labels";
import { API_BASE_URL_NO_API } from "../utils/apiConfig";

const API_BASE_URL = API_BASE_URL_NO_API;

export interface Supplier {
  id: number;
  name: string;
  email: string;
  phone: string;
}

export interface ProductBarcodeResponse {
  id: number;
  code: string;
  barcode_type: string;
  barcode_type_display: string;
  source: string;
  source_display: string;
  is_primary: boolean;
}

export type PickingStockRotation = "fifo" | "lifo";

export interface ProductApiResponse {
  id: number;
  name: string;
  description?: string;
  category: string[];
  product_code?: string;
  price: string;
  quantity: number;
  picking_stock_rotation?: PickingStockRotation;
  shelf_life_days?: number | null;
  expiry_warning_days?: number | null;
  block_expired_sales?: boolean;
  created_at: string;
  updated_at: string;
  created_by: number;
  created_by_name: string;
  updated_by: number;
  updated_by_name: string;
  caretaker?: number;
  caretaker_name?: string;
  suppliers: Supplier[];
  labels?: Label[];
  barcodes?: ProductBarcodeResponse[];
  primary_barcode?: ProductBarcodeResponse | null;
  is_bundle?: boolean;
  in_bundles_quantity?: number;
  bundle_components?: Array<{
    component_product_id: number;
    component_product_name: string;
    component_product_code: string;
    quantity_per_bundle: number;
  }>;
  used_in_bundles?: Array<{
    bundle_id: number;
    bundle_product_id: number;
    bundle_name: string;
    bundle_code: string;
    quantity_per_bundle: number;
  }>;
}

export type PaginatedProductsResponse = PaginatedResponse<ProductApiResponse>;

export interface GetProductsParams {
  page?: number;
  page_size?: number;
  search?: string;
  category?: string;
  supplier?: number;
  low_stock?: number;
  warehouse?: number;
  labels?: string; // Comma-separated label IDs
}

export interface CreateProductRequest {
  name: string;
  description?: string;
  category?: string[];
  product_code?: string;
  price: number | string;
  quantity?: number;
  caretaker_id?: number;
  supplier_ids?: number[];
  label_ids?: number[];
  variants?: Array<{ brand: string; color: string; description: string }>;
  warehouse_id?: number;
  picking_stock_rotation?: PickingStockRotation;
  shelf_life_days?: number | null;
  expiry_warning_days?: number | null;
  block_expired_sales?: boolean;
  /** ISO date string (YYYY-MM-DD) — optional explicit expiry for the first incoming batch. */
  initial_expiry_date?: string | null;
}

export interface UpdateProductRequest {
  name?: string;
  description?: string;
  category?: string[];
  product_code?: string;
  price?: number | string;
  quantity?: number;
  caretaker_id?: number;
  supplier_ids?: number[] | null;
  label_ids?: number[] | null;
  picking_stock_rotation?: PickingStockRotation;
  shelf_life_days?: number | null;
  expiry_warning_days?: number | null;
  block_expired_sales?: boolean;
}

export interface UpdateQuantityRequest {
  quantity_change: number;
}

export interface ProductDocumentInfo {
  id: number;
  document_number: string;
  document_type: string;
  document_type_display: string;
  document_status: string;
  document_date: string;
  quantity_in_document: number;
  location_name: string | null;
  storage_location_code: string | null;
}

export type PaginatedProductDocumentsResponse = PaginatedResponse<ProductDocumentInfo>;

export interface PzWzDocRef {
  line_id: number;
  document_id: number;
  document_number: string;
  document_type: string;
  document_type_display: string;
  document_date: string;
  document_status: string;
  product_id: number;
  product_name: string;
  product_code: string;
  storage_location_code: string | null;
  quantity?: number;
  allocation_id?: number;
}

export interface PzWzReceipt extends PzWzDocRef {
  received_quantity: number;
  issued_quantity: number;
  remaining_quantity: number;
  outgoing: PzWzDocRef[];
}

export interface PzWzOutgoingLine extends PzWzDocRef {
  quantity: number;
  allocated_quantity: number;
  unallocated_quantity: number;
  sources: PzWzDocRef[];
}

export interface ProductPzWzAllocationsResponse {
  receipts: PzWzReceipt[];
  unallocated_outgoing: PzWzDocRef[];
}

export interface DocumentPzWzAllocationsResponse {
  view: "source" | "outgoing" | "mixed" | "none";
  receipts: PzWzReceipt[];
  outgoing_lines: PzWzOutgoingLine[];
}

export interface GetProductDocumentsParams {
  page?: number;
  page_size?: number;
  search?: string;
}

const getBaseUrl = () => {
  return API_BASE_URL.endsWith('/api') ? API_BASE_URL.slice(0, -4) : API_BASE_URL;
};

export const productsApi = {
  /**
   * Pobiera listę produktów z paginacją
   * @param params - Opcjonalne parametry wyszukiwania i paginacji
   * @returns Paginowana odpowiedź z API
   */
  getProducts: async (params?: GetProductsParams): Promise<PaginatedProductsResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.page) {
      queryParams.append("page", params.page.toString());
    }
    if (params?.page_size) {
      queryParams.append("page_size", params.page_size.toString());
    }
    if (params?.search) {
      queryParams.append("search", params.search);
    }
    if (params?.category) {
      queryParams.append("category", params.category);
    }
    if (params?.supplier) {
      queryParams.append("supplier", params.supplier.toString());
    }
    if (params?.low_stock !== undefined) {
      queryParams.append("low_stock", params.low_stock.toString());
    }
    if (params?.warehouse !== undefined) {
      queryParams.append("warehouse", params.warehouse.toString());
    }
    if (params?.labels) {
      queryParams.append("labels", params.labels);
    }

    const queryString = queryParams.toString();
    const baseUrl = getBaseUrl();
    const url = `${baseUrl}/api/products/${queryString ? `?${queryString}` : ""}`;

    const response = await axios.get<PaginatedProductsResponse>(url);
    return response.data;
  },

  /**
   * Pobiera szczegóły produktu po ID
   * @param id - ID produktu
   * @returns Szczegóły produktu
   */
  getProduct: async (id: number): Promise<ProductApiResponse> => {
    const baseUrl = getBaseUrl();
    const url = `${baseUrl}/api/products/${id}/`;
    const response = await axios.get<ProductApiResponse>(url);
    return response.data;
  },

  /**
   * Tworzy nowy produkt
   * @param data - Dane produktu do utworzenia
   * @returns Utworzony produkt
   */
  createProduct: async (data: CreateProductRequest): Promise<ProductApiResponse> => {
    const baseUrl = getBaseUrl();
    const url = `${baseUrl}/api/products/`;
    const response = await axios.post<ProductApiResponse>(url, data);
    return response.data;
  },

  /**
   * Aktualizuje produkt
   * @param id - ID produktu
   * @param data - Dane do aktualizacji (wszystkie pola opcjonalne)
   * @returns Zaktualizowany produkt
   */
  updateProduct: async (id: number, data: UpdateProductRequest): Promise<ProductApiResponse> => {
    const baseUrl = getBaseUrl();
    const url = `${baseUrl}/api/products/${id}/`;
    const response = await axios.patch<ProductApiResponse>(url, data);
    return response.data;
  },

  /**
   * Usuwa produkt
   * @param id - ID produktu do usunięcia
   */
  deleteProduct: async (id: number): Promise<void> => {
    const baseUrl = getBaseUrl();
    const url = `${baseUrl}/api/products/${id}/`;
    await axios.delete(url);
  },

  /**
   * Zmienia ilość produktu
   * @param id - ID produktu
   * @param quantityChange - Zmiana ilości (dodatnia = zwiększ, ujemna = zmniejsz)
   * @returns Zaktualizowany produkt
   */
  updateProductQuantity: async (id: number, quantityChange: number): Promise<ProductApiResponse> => {
    const baseUrl = getBaseUrl();
    const url = `${baseUrl}/api/products/${id}/update_quantity/`;
    const response = await axios.post<ProductApiResponse>(url, { quantity_change: quantityChange });
    return response.data;
  },

  /**
   * Pobiera produkty z niskim stanem
   * @param threshold - Próg niskiego stanu (domyślnie 10)
   * @returns Lista produktów z niskim stanem
   */
  getLowStockProducts: async (threshold?: number): Promise<ProductApiResponse[]> => {
    const queryParams = new URLSearchParams();
    if (threshold !== undefined) {
      queryParams.append("threshold", threshold.toString());
    }

    const queryString = queryParams.toString();
    const baseUrl = getBaseUrl();
    const url = `${baseUrl}/api/products/low_stock/${queryString ? `?${queryString}` : ""}`;

    const response = await axios.get<ProductApiResponse[]>(url);
    return response.data;
  },

  /**
   * Pobiera produkty po kategorii
   * @param category - Nazwa kategorii
   * @returns Lista produktów w danej kategorii
   */
  getProductsByCategory: async (category: string): Promise<ProductApiResponse[]> => {
    const baseUrl = getBaseUrl();
    const url = `${baseUrl}/api/products/by_category/?category=${encodeURIComponent(category)}`;
    const response = await axios.get<ProductApiResponse[]>(url);
    return response.data;
  },

  getProductDocuments: async (
    id: number,
    params?: GetProductDocumentsParams
  ): Promise<PaginatedProductDocumentsResponse> => {
    const baseUrl = getBaseUrl();
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) {
      queryParams.append("page", (params.page + 1).toString());
    }
    if (params?.page_size !== undefined) {
      queryParams.append("page_size", params.page_size.toString());
    }
    if (params?.search) {
      queryParams.append("search", params.search);
    }
    const queryString = queryParams.toString();
    const url = `${baseUrl}/api/products/${id}/documents/${queryString ? `?${queryString}` : ""}`;
    const response = await axios.get<ProductDocumentInfo[] | PaginatedProductDocumentsResponse>(url);
    const data = response.data;
    // Backend może zwracać tablicę (bez paginacji) – wtedy slice po stronie klienta
    if (Array.isArray(data)) {
      const page = params?.page ?? 0;
      const pageSize = params?.page_size ?? 10;
      const start = page * pageSize;
      const results = data.slice(start, start + pageSize);
      const hasNext = start + pageSize < data.length;
      const hasPrev = page > 0;
      return {
        count: data.length,
        results,
        next: hasNext ? "" : null,
        previous: hasPrev ? "" : null,
      };
    }
    // Backend zwraca { count, next, previous, results }
    return data;
  },

  getPzWzAllocations: async (
    id: number,
    warehouseId?: number
  ): Promise<ProductPzWzAllocationsResponse> => {
    const baseUrl = getBaseUrl();
    const queryParams = new URLSearchParams();
    if (warehouseId !== undefined) {
      queryParams.append("warehouse", warehouseId.toString());
    }
    const queryString = queryParams.toString();
    const url = `${baseUrl}/api/products/${id}/pz-wz-allocations/${queryString ? `?${queryString}` : ""}`;
    const response = await axios.get<ProductPzWzAllocationsResponse>(url);
    return response.data;
  },
};

